import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../Icon/Icon';

const MAX_VISIBLE_PAGES = 4;
const TOTAL_PAGES_THRESHOLD = 6;
const END_PAGES_COUNT = 4;

/**
 * Pagination Component
 *
 * Displays pagination controls with Next/Previous buttons and smart page numbering.
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current active page (1-based)
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback when page changes
 */
export const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  // Generate page numbers to display
  const pageNumbers = useMemo(() => {
    const pages = [];

    if (totalPages <= TOTAL_PAGES_THRESHOLD) {
      // If total pages is small, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Complex logic for larger page counts
      if (currentPage <= MAX_VISIBLE_PAGES) {
        // Start: 1 2 3 4 ... Last
        for (let i = 1; i <= MAX_VISIBLE_PAGES; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - (END_PAGES_COUNT - 1)) {
        // End: 1 ... Total-4 Total-3 Total-2 Total-1 Total
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - END_PAGES_COUNT; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle: 1 ... Current-1 Current Current+1 ... Last
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className='flex-justify-center'>
      <button
        className='pagination-btn nav-btn'
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label='Previous page'
      >
        <Icon name='Chevron_Left' size='xs' />
        <span>Prev</span>
      </button>

      {pageNumbers.map((page, index) => {
        if (page === '...') {
          return (
            <span key={`dots-${index}`} className='pagination-btn dots'>
              ...
            </span>
          );
        }

        return (
          <button
            key={page}
            className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </button>
        );
      })}

      <button
        className='pagination-btn nav-btn'
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label='Next page'
      >
        <span>Next</span>
        <Icon name='Chevron_Right' size='xs' />
      </button>
    </div>
  );
};

Pagination.displayName = 'Pagination';

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
